import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { formatCellDisplay, getColumnMeta } from './columnMeta'
import type { ReportRow } from '../lib/types'
import { reportEyebrow } from './ui'

interface ReportRowDrawerProps {
  row: ReportRow | null
  columns: string[]
  onClose: () => void
}

export function ReportRowDrawer({ row, columns, onClose }: ReportRowDrawerProps) {
  useEffect(() => {
    if (!row) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [row, onClose])

  // Re-key the detail list when the row swaps while the drawer stays open, so the
  // field entrance replays for the new record.
  const epochRef = useRef(0)
  const prevRowRef = useRef<ReportRow | null>(null)
  if (prevRowRef.current !== row) {
    prevRowRef.current = row
    epochRef.current += 1
  }

  if (!row) return null

  return (
    <>
      <div
        className="fixed inset-0 z-40 animate-fade-in bg-slate-900/20 lg:bg-transparent"
        onClick={onClose}
      />
      <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-md animate-drawer-in flex-col border-l border-slate-200 bg-white shadow-xl ring-1 ring-black/5">
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-6 py-4">
          <div>
            <div className={reportEyebrow}>Row detail</div>
            <div className="mt-1 font-mono text-sm text-slate-900">
              {String(row.vehicle_id ?? row.driver_id ?? row.day ?? row.kind ?? 'Record')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 transition-[color,transform] duration-100 hover:text-slate-700 active:scale-95"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          <dl key={epochRef.current} className="divide-y divide-slate-200">
            {columns.map((col, i) => {
              const meta = getColumnMeta(col, row[col])
              return (
                <div
                  key={col}
                  className="animate-row-in py-3"
                  style={{ animationDelay: `${Math.min(i, 12) * 14}ms` }}
                >
                  <dt className={reportEyebrow}>
                    {meta.label ?? col.replace(/_/g, ' ')}
                  </dt>
                  <dd className="mt-1 font-mono text-sm tabular-nums text-slate-900">
                    {formatCellDisplay(row[col], col, meta)}
                  </dd>
                </div>
              )
            })}
          </dl>
        </div>
      </aside>
    </>
  )
}
